import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Card, Col, Form, Input, message, Modal, Row, Select, Table, Tag, Typography, Alert } from 'antd';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getClasses } from 'app/entities/classe/classe.reducer';
import { getMatieresByClasseId } from 'app/entities/matiere-ecole/matiere-ecole.reducer';
import { IDispenseCoursGroup } from 'app/shared/model/dispense-cours.model';
import {
  createDispensesCoursBatch,
  getDispenseModalClasseData,
  getDispensesCours,
  getDispenseSousMatieres,
  resetModalData,
  resetSousMatieres,
} from './dispense-cours.reducer';
import { buildDispenseInscriptionOptions, formatDispenseMatiereLabel, groupDispensesCours } from './dispenses-cours.utils';
import './eleve.scss';

const { Title } = Typography;

export const DispensesCours = (props: RouteComponentProps<{ ecoleid: string; anneeScolaireId: string }>) => {
  const dispatch = useAppDispatch();
  const classes = useAppSelector(state => state.classe.entities);
  const matieres = useAppSelector(state => state.matiereEcole.entities);
  const dispenseCoursState = useAppSelector(state => state.dispenseCours);
  const { ecoleid, anneeScolaireId } = props.match.params;
  const dispenses = dispenseCoursState.entities;
  const loading = dispenseCoursState.loading;
  const modalEleves = dispenseCoursState.modalEleves;
  const modalMatieres = dispenseCoursState.modalMatieres;
  const modalSousMatieres = dispenseCoursState.modalSousMatieres;
  const modalLoading = dispenseCoursState.modalLoading;
  const modalSousMatieresLoading = dispenseCoursState.modalSousMatieresLoading;
  const saving = dispenseCoursState.updating;
  const [classeId, setClasseId] = useState<string | null>(null);
  const [matiereId, setMatiereId] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalClasseId, setModalClasseId] = useState<string | null>(null);
  const [modalMatiereId, setModalMatiereId] = useState<string | null>(null);
  const [modalSousMatiereId, setModalSousMatiereId] = useState<string | null>(null);
  const [studentSearch, setStudentSearch] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(getClasses(ecoleid));
  }, [ecoleid]);

  useEffect(() => {
    if (classeId) {
      dispatch(getMatieresByClasseId(classeId));
    }
    setMatiereId(null);
  }, [classeId]);

  useEffect(() => {
    dispatch(getDispensesCours({ ecoleId: ecoleid, anneeScolaireId }));
  }, [ecoleid, anneeScolaireId]);

  const selectedModalMatiere = useMemo(() => modalMatieres.find(matiere => matiere.id === modalMatiereId), [modalMatieres, modalMatiereId]);

  const groups = useMemo(() => groupDispensesCours(dispenses), [dispenses]);

  const filteredGroups = useMemo(
    () =>
      groups.filter(group => {
        if (classeId && group.classeId !== classeId) return false;
        if (matiereId && group.matiereId !== matiereId) return false;
        return true;
      }),
    [groups, classeId, matiereId]
  );

  const selectedModalGroup = useMemo(
    () =>
      groups.find(
        group =>
          group.classeId === modalClasseId &&
          group.matiereId === modalMatiereId &&
          (group.sousMatiereId || undefined) === (modalSousMatiereId || undefined)
      ),
    [groups, modalClasseId, modalMatiereId, modalSousMatiereId]
  );

  const inscriptionOptions = useMemo(
    () =>
      buildDispenseInscriptionOptions(modalEleves, dispenses, {
        classeId: modalClasseId,
        matiereId: modalMatiereId,
        sousMatiereId: modalSousMatiereId,
        search: studentSearch,
      }),
    [modalEleves, dispenses, modalClasseId, modalMatiereId, modalSousMatiereId, studentSearch]
  );

  const resetModal = () => {
    form.resetFields();
    setModalClasseId(null);
    setModalMatiereId(null);
    setModalSousMatiereId(null);
    setStudentSearch('');
    dispatch(resetModalData());
  };

  const handleModalClasseChange = (nextClasseId: string) => {
    setModalClasseId(nextClasseId || null);
    setModalMatiereId(null);
    setModalSousMatiereId(null);
    setStudentSearch('');
    dispatch(resetModalData());
    form.setFieldsValue({ inscriptionIds: [], matiereId: undefined, sousMatiereId: undefined, motif: undefined });
    if (nextClasseId) {
      dispatch(getDispenseModalClasseData({ ecoleId: ecoleid, anneeScolaireId, classeId: nextClasseId }));
    }
  };

  const handleModalMatiereChange = (nextMatiereId: string) => {
    setModalMatiereId(nextMatiereId || null);
    setModalSousMatiereId(null);
    setStudentSearch('');
    dispatch(resetSousMatieres());
    form.setFieldsValue({ sousMatiereId: undefined, inscriptionIds: [], motif: undefined });

    const nextMatiere = modalMatieres.find(matiere => matiere.id === nextMatiereId);
    if (!nextMatiere?.avecSousMatieres) {
      return;
    }

    dispatch(getDispenseSousMatieres({ cycleMatiereId: nextMatiere.cycleMatiereId, matiereEcoleId: nextMatiere.id }));
  };

  useEffect(() => {
    if (selectedModalGroup?.motifsMultiples) {
      form.setFieldsValue({ motif: undefined });
      return;
    }
    if (selectedModalGroup) {
      form.setFieldsValue({ motif: selectedModalGroup.motif });
    }
  }, [selectedModalGroup]);

  const handleCreateDispense = async values => {
    try {
      await dispatch(
        createDispensesCoursBatch({
          ecoleId: ecoleid,
          anneeScolaireId,
          classeId: values.classeId,
          matiereId: values.matiereId,
          sousMatiereId: values.sousMatiereId,
          inscriptionIds: values.inscriptionIds,
          motif: values.motif,
        })
      ).unwrap();
      message.success('Dispenses ajoutées');
      setModalVisible(false);
      resetModal();
      dispatch(getDispensesCours({ ecoleId: ecoleid, anneeScolaireId }));
    } catch {
      message.error('Impossible de créer les dispenses. Vérifiez les élèves sélectionnés, la matière et le motif du groupe.');
    }
  };

  const openDetails = (group: IDispenseCoursGroup) => {
    const query = group.sousMatiereId ? `?sousMatiereId=${encodeURIComponent(group.sousMatiereId)}` : '';
    props.history.push(`/eleve/${ecoleid}/${anneeScolaireId}/dispenses/${group.classeId}/${group.matiereId}/details${query}`);
  };

  const columns = [
    { title: 'Classe', dataIndex: 'classeNom', key: 'classeNom' },
    {
      title: 'Matière',
      dataIndex: 'matiereNom',
      key: 'matiereNom',
      render: (_, record: IDispenseCoursGroup) => <Tag color="blue">{formatDispenseMatiereLabel(record)}</Tag>,
    },
    {
      title: 'Motif',
      dataIndex: 'motif',
      key: 'motif',
      render: (_, record: IDispenseCoursGroup) => (record.motifsMultiples ? 'Motifs multiples' : record.motif || '-'),
    },
    { title: 'Effectif', dataIndex: 'effectif', key: 'effectif' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record: IDispenseCoursGroup) => <Button type="text" icon={<EyeOutlined />} onClick={() => openDetails(record)}></Button>,
    },
  ];

  return (
    <Card style={{ margin: 24 }}>
      <Row justify="space-between" align="middle" gutter={[16, 16]}>
        <Col>
          <Title level={2}>Dispenses</Title>
        </Col>
        <Col>
          <Button type="primary" className="matiere-add-btn" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
            Nouvelle dispense
          </Button>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={6}>
          <Select
            placeholder="Classe"
            allowClear
            value={classeId || undefined}
            onChange={value => setClasseId(value || null)}
            style={{ width: '100%' }}
          >
            {(classes || []).map(classe => (
              <Select.Option key={classe.id} value={classe.id}>
                {classe.nom}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} md={6}>
          <Select
            placeholder="Matière"
            allowClear
            value={matiereId || undefined}
            onChange={value => setMatiereId(value || null)}
            style={{ width: '100%' }}
            disabled={!classeId}
          >
            {(matieres || []).map(matiere => (
              <Select.Option key={matiere.id} value={matiere.id}>
                {matiere.nom}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Table rowKey="key" loading={loading} columns={columns} dataSource={filteredGroups} />
      <Modal
        title="Nouvelle dispense"
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          resetModal();
        }}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText="Enregistrer"
        cancelText="Annuler"
        okButtonProps={{ className: 'matiere-add-btn' }}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateDispense}>
          <Form.Item label="Classe" name="classeId" rules={[{ required: true, message: 'Classe obligatoire' }]}>
            <Select placeholder="Sélectionner une classe" onChange={handleModalClasseChange} showSearch optionFilterProp="children">
              {(classes || []).map(classe => (
                <Select.Option key={classe.id} value={classe.id}>
                  {classe.nom}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Matière" name="matiereId" rules={[{ required: true, message: 'Matière obligatoire' }]}>
            <Select
              placeholder="Sélectionner une matière"
              disabled={!modalClasseId}
              loading={modalLoading}
              onChange={handleModalMatiereChange}
              showSearch
              optionFilterProp="children"
            >
              {modalMatieres.map(matiere => (
                <Select.Option key={matiere.id} value={matiere.id}>
                  {matiere.nom}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {selectedModalMatiere?.avecSousMatieres && modalSousMatieres.length > 0 && (
            <Form.Item label="Sous-matière" name="sousMatiereId">
              <Select
                placeholder="Sélectionner une sous-matière"
                allowClear
                loading={modalSousMatieresLoading}
                onChange={value => {
                  setModalSousMatiereId(value || null);
                  setStudentSearch('');
                  form.setFieldsValue({ inscriptionIds: [], motif: undefined });
                }}
                showSearch
                optionFilterProp="children"
              >
                {modalSousMatieres.map(sousMatiere => (
                  <Select.Option key={sousMatiere.id} value={sousMatiere.id}>
                    {sousMatiere.nom}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item
            label="Élèves inscrits"
            name="inscriptionIds"
            rules={[{ required: true, message: 'Au moins un élève est obligatoire' }]}
          >
            <Select
              mode="multiple"
              placeholder="Sélectionner des élèves"
              disabled={!modalClasseId || !modalMatiereId || selectedModalGroup?.motifsMultiples}
              loading={modalLoading}
              showSearch
              searchValue={studentSearch}
              onSearch={setStudentSearch}
              filterOption={false}
            >
              {inscriptionOptions.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {selectedModalGroup?.motifsMultiples && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Ce groupe contient plusieurs motifs historiques. Supprimez les écarts avant d’ajouter des élèves."
            />
          )}
          <Form.Item label="Motif" name="motif">
            <Input.TextArea
              placeholder="Motif de la dispense"
              rows={3}
              disabled={!!selectedModalGroup && !selectedModalGroup.motifsMultiples}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default DispensesCours;
